import React, { useEffect, useState, useRef } from "react";
import { Plus, Trash2, X, Linkedin, Twitter, Copy as CopyIcon, Eye, Upload, FileText, Activity as ActivityIcon, Download as DownloadIcon } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { api, getErrMsg } from "@/lib/api";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const EMPTY_DEAL = {
  title: "", country: "", region: "Africa", sector: "sovereign", deal_type: "debt",
  size_usd: "", tenor: "", return_text: "", return_low: 10, return_high: 15,
  status: "pre_market", tier_required: "standard",
  overview: "", structure: "", structure_flow: [], risks: [], pricing_insight: "",
  execution_path: [], min_ticket: "", timeline: "",
};

export default function Admin() {
  const [tab, setTab] = useState("deals");
  const [deals, setDeals] = useState([]);
  const [memos, setMemos] = useState([]);
  const [users, setUsers] = useState([]);
  const [accessReqs, setAccessReqs] = useState([]);
  const [allocReqs, setAllocReqs] = useState([]);
  const [shareStats, setShareStats] = useState(null);
  const [attributions, setAttributions] = useState(null);
  const [dealForm, setDealForm] = useState(null);
  const [activity, setActivity] = useState(null);
  const [activityFilters, setActivityFilters] = useState({
    days: 30,
    types: [],
    actor: "",
    deal_id: "",
  });
  const [activityEventTypes, setActivityEventTypes] = useState([]);

  const load = async () => {
    const [d, m, u, a, al, s, at] = await Promise.all([
      api.get("/deals"),
      api.get("/intelligence"),
      api.get("/admin/users"),
      api.get("/admin/access-requests"),
      api.get("/admin/allocation-requests"),
      api.get("/admin/share-stats"),
      api.get("/admin/attributions"),
    ]);
    setDeals(d.data);
    setMemos(m.data);
    setUsers(u.data);
    setAccessReqs(a.data);
    setAllocReqs(al.data);
    setShareStats(s.data);
    setAttributions(at.data);
  };

  useEffect(() => { load(); }, []);

  // Activity feed loaders (lazy — fetch only when tab is active or filters change)
  const loadActivity = async () => {
    const { days, types, actor, deal_id } = activityFilters;
    const qs = new URLSearchParams();
    qs.set("days", String(days));
    if (types.length) qs.set("types", types.join(","));
    if (actor.trim()) qs.set("actor", actor.trim());
    if (deal_id.trim()) qs.set("deal_id", deal_id.trim());
    try {
      const r = await api.get(`/admin/activity?${qs.toString()}`);
      setActivity(r.data);
    } catch (err) {
      toast.error("Activity load failed", { description: getErrMsg(err) });
    }
  };

  useEffect(() => {
    if (tab !== "activity") return;
    if (activityEventTypes.length === 0) {
      api.get("/admin/activity/event-types").then((r) => setActivityEventTypes(r.data.event_types));
    }
    loadActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, activityFilters]);

  const toggleActivityType = (t) => {
    setActivityFilters((f) => ({
      ...f,
      types: f.types.includes(t) ? f.types.filter((x) => x !== t) : [...f.types, t],
    }));
  };

  const exportActivityCsv = () => {
    if (!activity?.events?.length) return;
    const headers = ["created_at", "type", "deal_id", "deal_title", "actor_email", "message"];
    const escape = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = [headers.join(",")].concat(
      activity.events.map((e) => headers.map((h) => escape(e[h])).join(","))
    );
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `frontera-activity-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const deleteDeal = async (id) => {
    if (!window.confirm("Delete this deal?")) return;
    await api.delete(`/deals/${id}`);
    toast.success("Deal removed");
    load();
  };

  const saveDeal = async () => {
    try {
      const payload = {
        ...dealForm,
        return_low: parseFloat(dealForm.return_low) || 0,
        return_high: parseFloat(dealForm.return_high) || 0,
        structure_flow: typeof dealForm.structure_flow === "string"
          ? dealForm.structure_flow.split(/[,\n]/).map((s) => s.trim()).filter(Boolean)
          : dealForm.structure_flow,
        risks: typeof dealForm.risks === "string"
          ? dealForm.risks.split("\n").map((s) => s.trim()).filter(Boolean)
          : dealForm.risks,
        execution_path: typeof dealForm.execution_path === "string"
          ? dealForm.execution_path.split("\n").map((s) => s.trim()).filter(Boolean)
          : dealForm.execution_path,
      };
      if (dealForm.id) {
        await api.put(`/deals/${dealForm.id}`, payload);
      } else {
        await api.post("/deals", payload);
      }
      toast.success("Deal saved");
      setDealForm(null);
      load();
    } catch (err) {
      toast.error("Could not save", { description: getErrMsg(err) });
    }
  };

  const updateUserTier = async (id, tier) => {
    await api.patch(`/admin/users/${id}`, { tier });
    toast.success(`Tier updated to ${tier}`);
    load();
  };

  const deleteMemo = async (id) => {
    if (!window.confirm("Delete this memo?")) return;
    await api.delete(`/intelligence/${id}`);
    toast.success("Memo removed");
    load();
  };

  return (
    <DashboardLayout>
      <div className="border-b border-border px-6 md:px-10 py-6">
        <div className="overline gold-text">Admin Panel</div>
        <h1 className="font-display font-medium text-3xl md:text-4xl tracking-tight mt-1">Operations</h1>
      </div>

      <div className="px-6 md:px-10 py-8">
        <Tabs value={tab} onValueChange={setTab} data-testid="admin-tabs">
          <TabsList>
            <TabsTrigger value="deals" data-testid="admin-tab-deals">Deals ({deals.length})</TabsTrigger>
            <TabsTrigger value="memos" data-testid="admin-tab-memos">Memos ({memos.length})</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">Users ({users.length})</TabsTrigger>
            <TabsTrigger value="requests" data-testid="admin-tab-requests">Access ({accessReqs.length})</TabsTrigger>
            <TabsTrigger value="allocations" data-testid="admin-tab-allocations">Allocations ({allocReqs.length})</TabsTrigger>
            <TabsTrigger value="shares" data-testid="admin-tab-shares">Shares ({shareStats?.total_events ?? 0})</TabsTrigger>
            <TabsTrigger value="attributions" data-testid="admin-tab-attributions">Attributions ({attributions?.total ?? 0})</TabsTrigger>
            <TabsTrigger value="activity" data-testid="admin-tab-activity">Activity{activity ? ` (${activity.total})` : ""}</TabsTrigger>
          </TabsList>

          <TabsContent value="deals" className="mt-5">
            <button onClick={() => setDealForm({ ...EMPTY_DEAL })} className="btn-primary mb-4" data-testid="admin-new-deal">
              <Plus className="h-4 w-4" /> New Deal
            </button>
            <div className="card-institutional overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-4 py-3 text-left overline font-normal">Title</th>
                    <th className="px-4 py-3 text-left overline font-normal">Country</th>
                    <th className="px-4 py-3 text-left overline font-normal">Tier</th>
                    <th className="px-4 py-3 text-left overline font-normal">Status</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {deals.map((d) => (
                    <tr key={d.id} className="border-b border-border last:border-0">
                      <td className="px-4 py-3">{d.title}</td>
                      <td className="px-4 py-3 text-muted-foreground">{d.country}</td>
                      <td className="px-4 py-3 text-muted-foreground capitalize">{d.tier_required}</td>
                      <td className="px-4 py-3"><StatusBadge status={d.status} /></td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => setDealForm({ ...d })} className="text-xs mr-3 hover:text-foreground text-muted-foreground" data-testid={`admin-edit-${d.id}`}>Edit</button>
                        <button onClick={() => deleteDeal(d.id)} className="text-xs text-[hsl(var(--status-closed))]" data-testid={`admin-delete-${d.id}`}><Trash2 className="h-3.5 w-3.5 inline" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="memos" className="mt-5">
            <div className="card-institutional divide-y divide-border">
              {memos.map((m) => (
                <div key={m.id} className="p-4 flex items-center justify-between">
                  <div>
                    <div className="font-medium">{m.title}</div>
                    <div className="text-xs text-muted-foreground mt-1">{m.tag} · {m.read_time}</div>
                  </div>
                  <button onClick={() => deleteMemo(m.id)} className="text-xs text-[hsl(var(--status-closed))]" data-testid={`admin-delete-memo-${m.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="users" className="mt-5">
            <div className="card-institutional overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-4 py-3 text-left overline font-normal">Email</th>
                    <th className="px-4 py-3 text-left overline font-normal">Name</th>
                    <th className="px-4 py-3 text-left overline font-normal">Role</th>
                    <th className="px-4 py-3 text-left overline font-normal">Tier</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-border last:border-0">
                      <td className="px-4 py-3 font-mono text-xs">{u.email} {u.is_admin && <span className="ml-2 text-[10px] gold-text">ADMIN</span>}</td>
                      <td className="px-4 py-3">{u.name || "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{u.role || "—"}</td>
                      <td className="px-4 py-3">
                        <select
                          value={u.tier}
                          onChange={(e) => updateUserTier(u.id, e.target.value)}
                          className="h-8 bg-transparent border border-border rounded-md px-2 text-xs"
                          data-testid={`admin-user-tier-${u.id}`}
                        >
                          <option value="standard" className="bg-background">standard</option>
                          <option value="professional" className="bg-background">professional</option>
                          <option value="institutional" className="bg-background">institutional</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="requests" className="mt-5">
            <div className="space-y-3">
              {accessReqs.length === 0 && <p className="text-sm text-muted-foreground">No requests yet.</p>}
              {accessReqs.map((r) => (
                <div key={r.id} className="card-institutional p-4">
                  <div className="flex justify-between">
                    <div className="font-medium">{r.name}</div>
                    <div className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</div>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 font-mono">{r.email}</div>
                  <div className="text-sm mt-2">{r.role} · {r.capital_deployed}</div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="allocations" className="mt-5">
            <div className="space-y-3">
              {allocReqs.length === 0 && <p className="text-sm text-muted-foreground">No allocation requests yet.</p>}
              {allocReqs.map((r) => (
                <div key={r.id} className="card-institutional p-4">
                  <div className="flex justify-between">
                    <div className="font-medium">{r.deal_title}</div>
                    <div className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</div>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 font-mono">{r.user_email}</div>
                  <div className="text-sm mt-2">Ticket: <span className="font-mono gold-text">{r.ticket_size}</span></div>
                  {r.notes && <div className="text-xs text-muted-foreground mt-2">“{r.notes}”</div>}
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="shares" className="mt-5">
            {shareStats && (
              <>
                {/* Totals strip */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="card-institutional p-5" data-testid="share-total-events">
                    <div className="overline">Total Events</div>
                    <div className="font-mono text-2xl mt-2">{shareStats.total_events}</div>
                  </div>
                  <div className="card-institutional p-5" data-testid="share-total-linkedin">
                    <div className="overline flex items-center gap-1.5"><Linkedin className="h-3 w-3" /> LinkedIn</div>
                    <div className="font-mono text-2xl mt-2 gold-text">{shareStats.totals.linkedin || 0}</div>
                  </div>
                  <div className="card-institutional p-5" data-testid="share-total-twitter">
                    <div className="overline flex items-center gap-1.5"><Twitter className="h-3 w-3" /> X / Twitter</div>
                    <div className="font-mono text-2xl mt-2 gold-text">{shareStats.totals.twitter || 0}</div>
                  </div>
                  <div className="card-institutional p-5" data-testid="share-total-visits">
                    <div className="overline flex items-center gap-1.5"><Eye className="h-3 w-3" /> Inbound Visits</div>
                    <div className="font-mono text-2xl mt-2 gold-text">{shareStats.totals.visit || 0}</div>
                  </div>
                </div>

                <div className="card-institutional overflow-x-auto">
                  <table className="w-full text-sm" data-testid="share-stats-table">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="px-4 py-3 text-left overline font-normal">Entity</th>
                        <th className="px-4 py-3 text-left overline font-normal">Type</th>
                        <th className="px-4 py-3 text-right overline font-normal">LinkedIn</th>
                        <th className="px-4 py-3 text-right overline font-normal">X</th>
                        <th className="px-4 py-3 text-right overline font-normal">Copy</th>
                        <th className="px-4 py-3 text-right overline font-normal">Visits</th>
                        <th className="px-4 py-3 text-right overline font-normal">Total</th>
                        <th className="px-4 py-3 text-right overline font-normal">Last</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shareStats.by_entity.length === 0 && (
                        <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">No share events yet. When users share deals/memos/playbooks, stats appear here.</td></tr>
                      )}
                      {shareStats.by_entity.map((r) => (
                        <tr key={`${r.type}-${r.entity_id}`} className="border-b border-border last:border-0" data-testid={`share-row-${r.entity_id}`}>
                          <td className="px-4 py-3 font-medium">{r.title || r.entity_id}</td>
                          <td className="px-4 py-3 text-muted-foreground capitalize">{r.type}</td>
                          <td className="px-4 py-3 font-mono text-right">{r.linkedin || 0}</td>
                          <td className="px-4 py-3 font-mono text-right">{r.twitter || 0}</td>
                          <td className="px-4 py-3 font-mono text-right">{r.copy || 0}</td>
                          <td className="px-4 py-3 font-mono text-right">{r.visit || 0}</td>
                          <td className="px-4 py-3 font-mono text-right gold-text">{r.total}</td>
                          <td className="px-4 py-3 font-mono text-right text-xs text-muted-foreground">
                            {r.last_at ? new Date(r.last_at).toLocaleDateString() : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </TabsContent>
          <TabsContent value="attributions" className="mt-5">
            {attributions && (
              <>
                <p className="text-sm text-muted-foreground mb-5">
                  Users who signed up via a shared deal, memo or playbook — attributed by UTM + referral tracking.
                </p>

                {/* Top sources */}
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  {attributions.by_entity.slice(0, 3).map((r, i) => (
                    <div key={`${r.type}-${r.entity_id}`} className="card-institutional p-5" data-testid={`attribution-top-${i}`}>
                      <div className="overline">#{i + 1} · {r.type}</div>
                      <h3 className="font-display font-medium text-base mt-2 min-h-[2.5rem]">{r.title || r.entity_id}</h3>
                      <div className="flex items-baseline gap-2 mt-3">
                        <span className="font-mono text-2xl gold-text">{r.signups}</span>
                        <span className="text-xs text-muted-foreground">signups</span>
                      </div>
                    </div>
                  ))}
                  {attributions.by_entity.length === 0 && (
                    <div className="card-institutional p-5 md:col-span-3 text-center text-sm text-muted-foreground">
                      No attributions yet. When someone signs up via a shared link with UTM params, they appear here.
                    </div>
                  )}
                </div>

                {/* Users table */}
                <div className="card-institutional overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="px-4 py-3 text-left overline font-normal">Email</th>
                        <th className="px-4 py-3 text-left overline font-normal">Via</th>
                        <th className="px-4 py-3 text-left overline font-normal">Source</th>
                        <th className="px-4 py-3 text-left overline font-normal">Campaign</th>
                        <th className="px-4 py-3 text-right overline font-normal">Signed Up</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attributions.users.length === 0 && (
                        <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">No attributed signups yet.</td></tr>
                      )}
                      {attributions.users.map((u) => (
                        <tr key={u.id} className="border-b border-border last:border-0" data-testid={`attribution-user-${u.id}`}>
                          <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                          <td className="px-4 py-3 text-muted-foreground capitalize">
                            {u.attribution_entity_type} · {(u.attribution_entity_id || "").slice(-14)}
                          </td>
                          <td className="px-4 py-3 capitalize">
                            <span className="text-xs px-2 py-0.5 rounded-full border border-[hsl(var(--gold))] gold-text">
                              {u.attribution_source || "direct"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground text-xs font-mono">{u.attribution_campaign || "—"}</td>
                          <td className="px-4 py-3 font-mono text-right text-xs text-muted-foreground">
                            {u.attribution_at ? new Date(u.attribution_at).toLocaleDateString() : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="activity" className="mt-5">
            <div className="card-institutional p-5 mb-4" data-testid="activity-filters">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="overline block mb-2">Window (days)</label>
                  <select
                    value={activityFilters.days}
                    onChange={(e) => setActivityFilters({ ...activityFilters, days: Number(e.target.value) })}
                    className="w-full h-10 bg-transparent border border-border rounded-md px-3 text-sm outline-none"
                    data-testid="activity-filter-days"
                  >
                    {[1, 7, 30, 90, 365].map((d) => (
                      <option key={d} value={d} className="bg-background">Last {d} day{d !== 1 ? "s" : ""}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="overline block mb-2">Actor (email contains)</label>
                  <input
                    value={activityFilters.actor}
                    onChange={(e) => setActivityFilters({ ...activityFilters, actor: e.target.value })}
                    placeholder="pro@"
                    className="w-full h-10 bg-transparent border border-border rounded-md px-3 text-sm outline-none focus:border-[hsl(var(--gold))]"
                    data-testid="activity-filter-actor"
                  />
                </div>
                <div>
                  <label className="overline block mb-2">Deal ID (optional)</label>
                  <input
                    value={activityFilters.deal_id}
                    onChange={(e) => setActivityFilters({ ...activityFilters, deal_id: e.target.value })}
                    placeholder="pl-nigeria-juvent"
                    className="w-full h-10 bg-transparent border border-border rounded-md px-3 text-sm outline-none focus:border-[hsl(var(--gold))]"
                    data-testid="activity-filter-deal"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    onClick={exportActivityCsv}
                    disabled={!activity?.events?.length}
                    className="btn-ghost w-full"
                    data-testid="activity-export-csv"
                  >
                    <DownloadIcon className="h-4 w-4" /> Export CSV
                  </button>
                </div>
              </div>
              <div>
                <div className="overline mb-2">Event types</div>
                <div className="flex flex-wrap gap-2">
                  {activityEventTypes.map((t) => {
                    const on = activityFilters.types.includes(t);
                    const count = activity?.by_type?.[t] || 0;
                    return (
                      <button
                        key={t}
                        onClick={() => toggleActivityType(t)}
                        className={`px-3 py-1.5 text-[11px] font-mono tracking-wider rounded-full border transition-colors ${
                          on
                            ? "border-[hsl(var(--gold))] bg-[hsl(var(--gold))]/10 gold-text"
                            : "border-border text-muted-foreground hover:border-[hsl(var(--gold))]/40"
                        }`}
                        data-testid={`activity-chip-${t}`}
                      >
                        {t.replaceAll("_", " ").toUpperCase()}
                        {count > 0 && <span className="ml-1.5 opacity-70">· {count}</span>}
                      </button>
                    );
                  })}
                  {activityFilters.types.length > 0 && (
                    <button
                      onClick={() => setActivityFilters({ ...activityFilters, types: [] })}
                      className="px-3 py-1.5 text-[11px] font-mono tracking-wider rounded-full border border-border text-muted-foreground hover:text-foreground"
                      data-testid="activity-chip-clear"
                    >
                      × clear
                    </button>
                  )}
                </div>
              </div>
            </div>

            {activity && (
              <div className="mb-4 text-sm text-muted-foreground flex items-center gap-3">
                <ActivityIcon className="h-3.5 w-3.5 gold-text" />
                <span data-testid="activity-summary">
                  {activity.returned} of {activity.total} event{activity.total !== 1 ? "s" : ""} in the last {activity.days} day{activity.days !== 1 ? "s" : ""}
                </span>
              </div>
            )}

            <div className="card-institutional overflow-x-auto">
              <table className="w-full text-sm" data-testid="activity-table">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-4 py-3 text-left overline font-normal">When</th>
                    <th className="px-4 py-3 text-left overline font-normal">Type</th>
                    <th className="px-4 py-3 text-left overline font-normal">Deal</th>
                    <th className="px-4 py-3 text-left overline font-normal">Actor</th>
                    <th className="px-4 py-3 text-left overline font-normal">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {!activity?.events?.length && (
                    <tr>
                      <td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">
                        No activity in this window. Adjust filters or wait for events to flow in.
                      </td>
                    </tr>
                  )}
                  {activity?.events?.map((e) => (
                    <tr key={e.id} className="border-b border-border last:border-0" data-testid={`activity-row-${e.id}`}>
                      <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground whitespace-nowrap">
                        {new Date(e.created_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-[10px] tracking-wider px-2 py-0.5 rounded-full border border-[hsl(var(--gold))]/40 gold-text">
                          {e.type.replaceAll("_", " ").toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium truncate max-w-[260px]">{e.deal_title}</div>
                        <div className="font-mono text-[10px] text-muted-foreground truncate">{e.deal_id}</div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{e.actor_email || "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{e.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Deal editor */}
      <Dialog open={!!dealForm} onOpenChange={(v) => !v && setDealForm(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-deal-dialog">
          <DialogHeader>
            <DialogTitle>{dealForm?.id ? "Edit Deal" : "New Deal"}</DialogTitle>
          </DialogHeader>
          {dealForm && (
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Title" value={dealForm.title} onChange={(v) => setDealForm({ ...dealForm, title: v })} testid="df-title" full />
              <Field label="Country" value={dealForm.country} onChange={(v) => setDealForm({ ...dealForm, country: v })} testid="df-country" />
              <Field label="Region" value={dealForm.region} onChange={(v) => setDealForm({ ...dealForm, region: v })} testid="df-region" />
              <Select label="Sector" value={dealForm.sector} onChange={(v) => setDealForm({ ...dealForm, sector: v })} options={["sovereign","corporate_debt","trade_finance","infrastructure","private_credit"]} testid="df-sector" />
              <Select label="Type" value={dealForm.deal_type} onChange={(v) => setDealForm({ ...dealForm, deal_type: v })} options={["debt","equity","hybrid"]} testid="df-type" />
              <Field label="Size" value={dealForm.size_usd} onChange={(v) => setDealForm({ ...dealForm, size_usd: v })} testid="df-size" />
              <Field label="Tenor" value={dealForm.tenor} onChange={(v) => setDealForm({ ...dealForm, tenor: v })} testid="df-tenor" />
              <Field label="Return text" value={dealForm.return_text} onChange={(v) => setDealForm({ ...dealForm, return_text: v })} testid="df-return-text" />
              <Field label="Return low (%)" value={dealForm.return_low} onChange={(v) => setDealForm({ ...dealForm, return_low: v })} testid="df-return-low" />
              <Field label="Return high (%)" value={dealForm.return_high} onChange={(v) => setDealForm({ ...dealForm, return_high: v })} testid="df-return-high" />
              <Select label="Status" value={dealForm.status} onChange={(v) => setDealForm({ ...dealForm, status: v })} options={["pre_market","live","closed"]} testid="df-status" />
              <Select label="Tier required" value={dealForm.tier_required} onChange={(v) => setDealForm({ ...dealForm, tier_required: v })} options={["standard","professional","institutional"]} testid="df-tier" />
              <Field label="Min ticket" value={dealForm.min_ticket} onChange={(v) => setDealForm({ ...dealForm, min_ticket: v })} testid="df-min" />
              <Field label="Timeline" value={dealForm.timeline} onChange={(v) => setDealForm({ ...dealForm, timeline: v })} testid="df-timeline" />
              <Field label="Structure summary" value={dealForm.structure} onChange={(v) => setDealForm({ ...dealForm, structure: v })} testid="df-structure" full />
              <Field label="Structure flow (comma-sep)" value={Array.isArray(dealForm.structure_flow) ? dealForm.structure_flow.join(", ") : dealForm.structure_flow} onChange={(v) => setDealForm({ ...dealForm, structure_flow: v })} testid="df-flow" full />
              <Field label="Overview" value={dealForm.overview} onChange={(v) => setDealForm({ ...dealForm, overview: v })} testid="df-overview" full textarea />
              <Field label="Pricing insight" value={dealForm.pricing_insight} onChange={(v) => setDealForm({ ...dealForm, pricing_insight: v })} testid="df-pricing" full textarea />
              <Field label="Risks (one per line)" value={Array.isArray(dealForm.risks) ? dealForm.risks.join("\n") : dealForm.risks} onChange={(v) => setDealForm({ ...dealForm, risks: v })} testid="df-risks" full textarea />
              <Field label="Execution path (one per line)" value={Array.isArray(dealForm.execution_path) ? dealForm.execution_path.join("\n") : dealForm.execution_path} onChange={(v) => setDealForm({ ...dealForm, execution_path: v })} testid="df-exec" full textarea />
              {dealForm.id && (
                <div className="md:col-span-2">
                  <div className="overline mb-2">Deal Room Documents</div>
                  <AttachmentsManager dealId={dealForm.id} initial={dealForm.attachments || []} onChange={(atts) => setDealForm({ ...dealForm, attachments: atts })} />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setDealForm(null)} className="btn-ghost" data-testid="admin-deal-cancel">Cancel</button>
            <button onClick={saveDeal} className="btn-primary" data-testid="admin-deal-save">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

const Field = ({ label, value, onChange, testid, full, textarea }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <label className="overline block mb-2">{label}</label>
    {textarea ? (
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-[hsl(var(--gold))]"
        data-testid={testid}
      />
    ) : (
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 bg-transparent border border-border rounded-md px-3 text-sm outline-none focus:border-[hsl(var(--gold))]"
        data-testid={testid}
      />
    )}
  </div>
);

const Select = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="overline block mb-2">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-10 bg-transparent border border-border rounded-md px-3 text-sm outline-none"
      data-testid={testid}
    >
      {options.map((o) => (
        <option key={o} value={o} className="bg-background">{o.replaceAll("_", " ")}</option>
      ))}
    </select>
  </div>
);

const AttachmentsManager = ({ dealId, initial, onChange }) => {
  const [items, setItems] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const [tier, setTier] = useState("professional");
  const [title, setTitle] = useState("");
  const fileRef = useRef(null);

  const refresh = async () => {
    const r = await api.get(`/deals/${dealId}/attachments`);
    setItems(r.data);
    onChange?.(r.data);
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [dealId]);

  const doUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("tier_required", tier);
    if (title) fd.append("title", title);
    setUploading(true);
    try {
      await api.post(`/deals/${dealId}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded");
      setTitle("");
      await refresh();
    } catch (err) {
      toast.error("Upload failed", { description: getErrMsg(err) });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (att) => {
    if (!window.confirm(`Remove ${att.filename}?`)) return;
    await api.delete(`/deals/${dealId}/attachments/${att.id}`);
    toast.success("Document removed");
    refresh();
  };

  const fmtSize = (b) => !b ? "" : b < 1024 ? `${b}B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(0)}KB` : `${(b / 1024 / 1024).toFixed(1)}MB`;

  return (
    <div className="space-y-3" data-testid="attachments-manager">
      <div className="card-institutional p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_160px] gap-3">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Document title (optional)"
            className="h-10 bg-transparent border border-border rounded-md px-3 text-sm outline-none focus:border-[hsl(var(--gold))]"
            data-testid="attachment-title-input"
          />
          <select
            value={tier}
            onChange={(e) => setTier(e.target.value)}
            className="h-10 bg-transparent border border-border rounded-md px-2 text-sm outline-none"
            data-testid="attachment-tier-select"
          >
            <option value="standard" className="bg-background">standard</option>
            <option value="professional" className="bg-background">professional</option>
            <option value="institutional" className="bg-background">institutional</option>
          </select>
        </div>
        <div>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
            onChange={doUpload}
            className="hidden"
            data-testid="attachment-file-input"
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="btn-primary"
            data-testid="attachment-upload-button"
          >
            <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload Document"}
          </button>
          <span className="text-xs text-muted-foreground ml-3">Max 25MB · PDF/DOCX/XLSX/PNG/JPG</span>
        </div>
      </div>
      {items.length > 0 && (
        <div className="card-institutional divide-y divide-border">
          {items.map((a) => (
            <div key={a.id} className="p-3 flex items-center justify-between gap-3" data-testid={`admin-attachment-${a.id}`}>
              <div className="flex items-start gap-3 min-w-0">
                <FileText className="h-4 w-4 gold-text mt-0.5 shrink-0" strokeWidth={1.5} />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{a.title}</div>
                  <div className="text-xs text-muted-foreground mt-0.5 font-mono truncate">
                    {a.filename} · {fmtSize(a.size)} · <span className="capitalize">{a.tier_required}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => remove(a)}
                className="text-xs text-[hsl(var(--status-closed))]"
                data-testid={`admin-attachment-delete-${a.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
